import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Send, Plus, Mic, Paperclip, Copy, RefreshCw, StopCircle, Trash2, Pin, Search } from "lucide-react";
import api from "@/lib/api";
import { GlassCard, SectionLabel, GradientButton } from "@/components/primitives";
import Markdown from "@/components/Markdown";

console.log("COPILOT COMPONENT LOADED");

const SUGGESTED = [
  "Summarize today's open incidents and recommend priorities.",
  "Generate an RCA template for a database failover event.",
  "Draft an on-call handoff for the EU region.",
  "Which runbook applies to OAuth refresh failures?",
];

function newSession() { return `s_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`; }

export default function Copilot() {
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [typing, setTyping] = useState("");
  const [filter, setFilter] = useState("");
  const [suggested, setSuggested] = useState([]);
  const stopRef = useRef(false);
  const bottomRef = useRef(null);
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const startVoice = () => {

    const SpeechRecognition =
        window.SpeechRecognition ||
        window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
        alert("Speech Recognition is not supported.");
        return;
    }

    const recognition = new SpeechRecognition();

    recognition.lang = "en-IN";

    recognition.continuous = false;
recognition.interimResults = true;

    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
        console.log("Voice started");
    };

    recognition.onspeechstart = () => {
        console.log("Speech detected");
    };

    recognition.onspeechend = () => {
        console.log("Speech ended");
    };
recognition.onresult = (event) => {

    let transcript = "";

    for (let i = event.resultIndex; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
    }

    console.log("Recognized:", transcript);

    setInput(transcript);
};

    recognition.onerror = (event) => {

        console.log("Speech Error:", event.error);

        if(event.error !== "no-speech"){
    console.error(event.error);
}

    };

    recognition.onnomatch = () => {

        console.log("No Match");

        alert("No speech recognized");

    };

    recognition.onend = () => {

        console.log("Recognition ended");

    };

    recognition.start();

};

  const loadSessions = async () => {
  try {
    const { data } = await api.get("/copilot/sessions");
    setSessions(data);
  } catch (e) {
    console.error(e);
  }
};

  const loadMessages = async (sid) => {
    try { const { data } = await api.get(`/copilot/sessions/${sid}`); setMessages(data); } catch { setMessages([]); }
  };

  useEffect(() => { loadSessions(); }, []);
useEffect(() => {
  if (!activeId) return;

  localStorage.setItem(
    "sentinel_copilot_session",
    activeId
  );

  loadMessages(activeId);
}, [activeId]);  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, typing]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    let sid = activeId;

if (!sid) {
    sid = newSession();
    setActiveId(sid);
}
    let uploadedExcerpt = "";

if (selectedFile) {

    const formData = new FormData();

    formData.append("file", selectedFile);

    const { data } = await api.post(
        "/documents/upload",
        formData,
        {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        }
    );

    uploadedExcerpt = data.excerpt || "";

    setSelectedFile(null);
}
    if (!msg || sending) return;
    setInput("");
setMessages((m) => [
    ...m,
    {
        id: `tmp-${Date.now()}`,
        role: "user",
        content: msg,
        ts: new Date().toISOString(),
        file: selectedFile
            ? {
                  name: selectedFile.name,
                  type: selectedFile.type,
              }
            : null,
    },
]);    setSending(true); stopRef.current = false; setTyping("");
    let asstId = null;
    let accumulated = "";
    try {
      const token = localStorage.getItem("sentinel_token");
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/copilot/chat/stream`;
      const ac = new AbortController();
      stopRef.current = false;
      const stopWatcher = setInterval(() => { if (stopRef.current) ac.abort(); }, 100);
      const resp = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
body: JSON.stringify({
    session_id: sid,
    message: uploadedExcerpt
        ? `${msg}

Attached document:

${uploadedExcerpt}`
        : msg,
}),        signal: ac.signal,
      });
      if (!resp.ok || !resp.body) throw new Error(`HTTP ${resp.status}`);
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      // Parse SSE: events separated by \n\n; each block has "event: X\ndata: {...}"
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buf.indexOf("\n\n")) !== -1) {
          const block = buf.slice(0, idx); buf = buf.slice(idx + 2);
          const lines = block.split("\n");
          let evt = "message"; let data = "";
          for (const ln of lines) {
            if (ln.startsWith("event:")) evt = ln.slice(6).trim();
            else if (ln.startsWith("data:")) data += ln.slice(5).trim();
          }
          if (!data) continue;
          try {
            const j = JSON.parse(data);
            if (evt === "start") asstId = j.id;
            else if (evt === "delta") { accumulated += j.t || ""; setTyping(accumulated); }
            else if (evt === "done") asstId = j.id || asstId;
          } catch (_) { /* ignore */ }
        }
      }
      clearInterval(stopWatcher);
      setMessages((m) => [...m, { id: asstId || `a-${Date.now()}`, role: "assistant", content: accumulated, ts: new Date().toISOString() }]);
      setTyping("");
      loadSessions();
      // Fetch suggested actions for the latest exchange
      try {
        const { data: sa } = await api.post("/copilot/suggest-actions", { text: msg });
        setSuggested(sa.actions || []);
      } catch (_) { /* ignore */ }
    } catch (e) {
      if (accumulated) {
        setMessages((m) => [...m, { id: asstId || `a-${Date.now()}`, role: "assistant", content: accumulated, ts: new Date().toISOString() }]);
      } else {
        setMessages((m) => [...m, { id: `err-${Date.now()}`, role: "assistant", content: `Sorry — Copilot stream was interrupted. ${e?.message || ""}`, ts: new Date().toISOString() }]);
      }
      setTyping("");
    } finally { setSending(false); }
  };

  const stop = () => { stopRef.current = true; setSending(false); setTyping(""); };
  const handleFileUpload = (event) => {
    const file = event.target.files[0];

    if (!file) return;

    setSelectedFile(file);
};

  const startNew = () => { const id = newSession(); setActiveId(id); setMessages([]); };
  const removeSession = async (sid) => {
    await api.delete(`/copilot/sessions/${sid}`).catch(() => {});
    if (sid === activeId) startNew();
    loadSessions();
  };

  const filtered = sessions.filter((s) => !filter || s.title.toLowerCase().includes(filter.toLowerCase()));

  return (
    <div className="grid lg:grid-cols-[280px_1fr] gap-5 h-[calc(100vh-9rem)]" data-testid="copilot-page">
      <GlassCard className="p-3 flex flex-col">
        <div className="px-2 py-1 flex items-center justify-between">
          <SectionLabel>Conversations</SectionLabel>
          <button data-testid="copilot-new-chat" onClick={startNew} className="p-1.5 rounded-lg hover:bg-white/5 text-cyan-300"><Plus className="h-4 w-4" /></button>
        </div>
        <div className="relative mt-2">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-slate-500" />
          <input
            data-testid="copilot-search-sessions"
            placeholder="Search history…" value={filter} onChange={(e) => setFilter(e.target.value)}
            className="w-full pl-8 pr-3 py-2 text-sm bg-white/[0.04] border border-white/10 rounded-lg focus:outline-none focus:border-cyan-400/50"
          />
        </div>
        <div className="mt-3 flex-1 overflow-auto scrollbar-thin pr-1 space-y-1">
          {filtered.length === 0 && <div className="text-xs text-slate-500 px-2 py-3">No chats yet.</div>}
          {filtered.map((s) => (
            <div key={s.session_id} className={`group flex items-center justify-between gap-2 p-2 rounded-lg cursor-pointer ${s.session_id === activeId ? "bg-white/[0.06] border border-white/10" : "hover:bg-white/[0.04] border border-transparent"}`} onClick={() => setActiveId(s.session_id)}>
              <div className="min-w-0">
                <div className="text-sm truncate">{s.title}</div>
                <div className="text-[10px] font-mono text-slate-500">{s.count} msgs</div>
              </div>
              <button data-testid={`copilot-delete-${s.session_id}`} onClick={(e) => { e.stopPropagation(); removeSession(s.session_id); }} className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-500/10 text-rose-300"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
      </GlassCard>

      <GlassCard className="p-0 flex flex-col">
        <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-cyan-400 via-blue-500 to-violet-500 grid place-items-center"><Sparkles className="h-4 w-4 text-white" /></div>
            <div>
              <div className="text-sm font-semibold">SentinelAI Copilot</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">GPT-5.2 · Enterprise</div>
            </div>
          </div>
          <button data-testid="copilot-pin" className="p-2 rounded-full hover:bg-white/10 text-slate-300"><Pin className="h-4 w-4" /></button>
        </div>

        <div className="flex-1 overflow-auto px-4 sm:px-8 py-6 space-y-5">
          {messages.length === 0 && !typing && (
            <div className="max-w-2xl mx-auto text-center">
              <div className="mx-auto h-14 w-14 rounded-2xl glass grid place-items-center"><Sparkles className="h-6 w-6 text-cyan-300" /></div>
              <h2 className="mt-4 text-2xl font-bold">How can I help you today?</h2>
              <p className="text-sm text-slate-400 mt-1">Ask anything about your operations, incidents, runbooks or documents.</p>
              <div className="mt-6 grid sm:grid-cols-2 gap-2">
                {SUGGESTED.map((q, i) => (
                  <button data-testid={`copilot-suggested-${i}`} key={i} onClick={() => send(q)} className="text-left text-sm p-3 rounded-xl bg-white/[0.03] border border-white/10 hover:border-cyan-400/40 hover:bg-white/[0.06] transition">{q}</button>
                ))}
              </div>
            </div>
          )}

          <AnimatePresence initial={false}>
            {messages.map((m) => (
              <motion.div key={m.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] p-4 rounded-2xl ${m.role === "user" ? "bg-[#1E293B] border border-white/5 rounded-tr-sm" : "bg-gradient-to-br from-cyan-500/[0.04] to-violet-500/[0.04] border border-white/10 rounded-tl-sm"}`}>
{m.role === "assistant" ? (

    <Markdown source={m.content} />

) : (

    <div>

        {m.file && (

            <div className="mb-2 flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-3 py-2">

                <Paperclip className="h-4 w-4 text-cyan-400" />

                <div>

                    <div className="text-sm font-medium text-white">
                        {m.file.name}
                    </div>

                    <div className="text-xs text-slate-400">
                        Attached document
                    </div>

                </div>

            </div>

        )}

        <div className="text-sm text-slate-100 whitespace-pre-wrap">
            {m.content}
        </div>

    </div>

)}                  {m.role === "assistant" && (
                    <div className="mt-2 flex items-center gap-1.5">
                      <button data-testid={`copilot-copy-${m.id}`} onClick={() => navigator.clipboard.writeText(m.content)} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-white/5 hover:bg-white/10 text-slate-400"><Copy className="inline h-3 w-3 mr-1" />Copy</button>
                      <button data-testid={`copilot-regen-${m.id}`} onClick={() => send(messages[messages.length - 2]?.content)} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-white/5 hover:bg-white/10 text-slate-400"><RefreshCw className="inline h-3 w-3 mr-1" />Regenerate</button>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {typing && (
            <div className="flex justify-start">
              <div className="max-w-[85%] p-4 rounded-2xl rounded-tl-sm border border-white/10 bg-gradient-to-br from-cyan-500/[0.04] to-violet-500/[0.04]">
                <Markdown source={typing} />
                <span className="typing-cursor" />
              </div>
            </div>
          )}

          {!sending && suggested.length > 0 && (
            <div className="flex flex-wrap gap-2 pl-1" data-testid="copilot-suggested-actions">
              {suggested.map((a, i) => (
                <button key={i} data-testid={`copilot-action-${i}`} onClick={() => send(a.label)} className="text-xs px-3 py-1.5 rounded-full bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-cyan-400/40 transition flex items-center gap-1.5">
                  <Sparkles className="h-3 w-3 text-cyan-300" /> {a.label}
                  <span className="text-[9px] font-mono uppercase tracking-wider text-slate-500">· {a.intent}</span>
                </button>
              ))}
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        <div className="px-4 sm:px-8 pb-6">
          <input
    type="file"
    hidden
    ref={fileInputRef}
    accept=".pdf,.doc,.docx,.txt"
    onChange={handleFileUpload}
/>
{selectedFile && (
    <div className="mx-4 sm:mx-8 mb-2 flex items-center justify-between rounded-lg bg-slate-800 border border-white/10 px-3 py-2">
        <div className="flex items-center gap-2">
            <Paperclip className="h-4 w-4 text-cyan-400" />
            <span className="text-sm text-slate-200">
                {selectedFile.name}
            </span>
        </div>

        <button
            onClick={() => setSelectedFile(null)}
            className="text-red-400 text-xs"
        >
            ✕
        </button>
    </div>
)}
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2 p-2 rounded-full bg-[#0F172A]/80 backdrop-blur-xl border border-white/10 shadow-lg">
            <button
    type="button"
    data-testid="copilot-attach"
    onClick={() => fileInputRef.current?.click()}
    className="p-2 rounded-full hover:bg-white/10 text-slate-400"
>
    <Paperclip className="h-4 w-4" />
</button>
            <button
type="button"
data-testid="copilot-voice"
onClick={startVoice}
className="p-2 rounded-full hover:bg-white/10 text-slate-400"><Mic className="h-4 w-4" /></button>
            <input
              data-testid="copilot-input"
              value={input} onChange={(e) => setInput(e.target.value)} disabled={sending}
              className="flex-1 bg-transparent outline-none px-2 text-sm placeholder:text-slate-500"
              placeholder="Ask SentinelAI anything…"
            />
            {sending
              ? <button type="button" data-testid="copilot-stop" onClick={stop} className="p-2 rounded-full bg-rose-500/20 text-rose-300 hover:bg-rose-500/30"><StopCircle className="h-4 w-4" /></button>
              : <GradientButton type="submit" data-testid="copilot-send" className="px-4 py-2"><Send className="h-4 w-4" /></GradientButton>}
          </form>
        </div>
      </GlassCard>
    </div>
  );
}
